import { chunk } from 'es-toolkit';

import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import {
    CatalogFeaturedLot,
    fetchItemSummariesByIds,
    getCatalogFeaturedLots,
} from '@/redux/modules/item/summary/itemSummary.api';
import { ItemSummary } from '@/types/item/ItemSummary';

import { shouldFetchItemSummary, whichItemSummariesNeeded } from './itemSummary.selectors';
import { FetchItemSummariesPayload, FetchItemSummaryPayload, ITEM_SUMMARY_FETCH_CHUNK_SIZE } from './itemSummary.types';

export const fetchCatalogFeaturedLots = createAsyncThunk<CatalogFeaturedLot[], { catalogId: number }>(
    'la/ui/itemSummary/fetchCatalogFeaturedLots',
    async ({ catalogId }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        return getCatalogFeaturedLots({ authToken, catalogId, deployment });
    }
);

export const fetchItemSummaries = createAsyncThunk<ItemSummary[], FetchItemSummariesPayload>(
    'la/ui/itemSummary/fetchItemSummaries',
    async ({ identifier, itemIds, liveStateFetch = false, similarItems }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const { payload } = await fetchItemSummariesByIds({
            authToken,
            deployment,
            identifier,
            itemIds,
            liveStateFetch,
            similarItems,
        });
        return payload.items;
    }
);

export const fetchItemSummaryIfNeeded = createAsyncThunk<void, FetchItemSummaryPayload>(
    'la/ui/itemSummary/fetchItemSummaryIfNeeded',
    async ({ force, identifier, itemId, liveStateFetch = false }, { dispatch, getState }) => {
        if (force || shouldFetchItemSummary(getState(), itemId)) {
            await dispatch(fetchItemSummaries({ identifier, itemIds: [itemId], liveStateFetch }));
        }
    }
);

export const fetchItemSummariesIfNeeded = createAsyncThunk<void, FetchItemSummariesPayload>(
    'la/ui/itemSummary/fetchItemSummariesIfNeeded',
    async ({ force, identifier, itemIds, liveStateFetch, similarItems }, { dispatch, getState }) => {
        const needed = force ? itemIds : whichItemSummariesNeeded(getState(), itemIds);
        if (needed.length > 0) {
            // If the array of items is too long we won't be able to send it.
            // We'll chunk it up into blocks of 400 that we can request separately
            const chunks = chunk(needed, ITEM_SUMMARY_FETCH_CHUNK_SIZE);
            const promises = chunks.map((chunk) =>
                dispatch(
                    fetchItemSummaries({
                        identifier,
                        itemIds: chunk,
                        liveStateFetch,
                        similarItems,
                    })
                )
            );
            await Promise.all(promises);
        }
        return Promise.resolve();
    }
);
